import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { Button } from './ui';
import { useUpdateApplicationResume, useUploadApplicationResume } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';

export interface ResumeUploadPanelProps {
  applicationId: number;
  candidateId: number;
  existingText?: string | null;
  onSaved?: (resumeText: string) => void;
  onCancel: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = '.pdf,.docx,.doc,.txt';
const ACCEPTED_MIMES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
];

export const ResumeUploadPanel: React.FC<ResumeUploadPanelProps> = ({
  applicationId,
  candidateId: _candidateId,
  existingText,
  onSaved,
  onCancel,
}) => {
  const [mode, setMode] = useState<'paste' | 'upload'>('paste');
  const [text, setText] = useState(existingText || '');
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const pasteMutation = useUpdateApplicationResume();
  const uploadMutation = useUploadApplicationResume();
  const isLoading = pasteMutation.isPending || uploadMutation.isPending;

  const handlePasteSave = useCallback(async () => {
    setError(null);
    if (text.trim().length < 50) {
      setError('Resume text must be at least 50 characters.');
      return;
    }
    try {
      const result = await pasteMutation.mutateAsync({ applicationId, resumeText: text });
      showToast('Resume saved. You can now run AI review.', 'success');
      onSaved?.(result.resumeText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save resume');
    }
  }, [text, applicationId, pasteMutation, showToast, onSaved]);

  const handleFileUpload = useCallback(async (file: File) => {
    setError(null);

    if (!ACCEPTED_MIMES.includes(file.type) && !file.name.endsWith('.docx') && !file.name.endsWith('.pdf') && !file.name.endsWith('.txt')) {
      setError('File type not supported. Accepted: PDF, DOCX, TXT');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 5 MB.`);
      return;
    }

    try {
      const result = await uploadMutation.mutateAsync({ applicationId, file });
      showToast(`Resume parsed (${result.characterCount.toLocaleString()} characters). You can now run AI review.`, 'success');
      onSaved?.(result.resumeText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload resume');
    }
  }, [applicationId, uploadMutation, showToast, onSaved]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  return (
    <div className="space-y-4">
      {/* Tab toggle */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
        <button
          type="button"
          onClick={() => { setMode('paste'); setError(null); }}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            mode === 'paste' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
          }`}
          disabled={isLoading}
        >
          Paste Text
        </button>
        <button
          type="button"
          onClick={() => { setMode('upload'); setError(null); }}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            mode === 'upload' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
          }`}
          disabled={isLoading}
        >
          Upload File
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Paste tab */}
      {mode === 'paste' && (
        <div className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the candidate's resume text here..."
            rows={12}
            disabled={isLoading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              disabled:bg-gray-100 disabled:cursor-not-allowed resize-y"
          />
          <div className="flex items-center justify-between">
            <span className={`text-xs ${text.length < 50 ? 'text-amber-600' : 'text-gray-500'}`}>
              {text.length.toLocaleString()} characters {text.length < 50 ? '(min 50)' : ''}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePasteSave}
                disabled={isLoading || text.trim().length < 50}
                loading={pasteMutation.isPending}
              >
                {existingText ? 'Save Changes' : 'Save Resume'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload tab */}
      {mode === 'upload' && (
        <div className="space-y-3">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${isLoading ? 'pointer-events-none opacity-50' : ''}
            `}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileSelect}
              className="hidden"
            />
            {uploadMutation.isPending ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                <p className="text-sm text-gray-600">Parsing file...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Drop a file here, or <span className="text-blue-600 font-medium">browse</span>
                </p>
                <p className="text-xs text-gray-400">PDF, DOCX, or TXT (max 5 MB)</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

ResumeUploadPanel.displayName = 'ResumeUploadPanel';
